import { ApiError, apiErrorCode } from "@/lib/api";

// BRK-S371 — an EIN that another active account in the broker's brokerage
// already holds (PMS-S758). A write that would set the insured's EIN — a
// pushing bond step save, or the account record's own save — answers 409:
//
//   { error: "Another account in your brokerage already has this EIN.",
//     details: { code: "EIN_ALREADY_EXISTS", field: "business.ein",
//                accountId?, accountNumber?, legalName? } }
//
// Before the code existed the 409 arrived bare and read as "This changed since
// you opened it. Reload the page and try again." (REFUSAL_BY_STATUS) — advice no
// reload can satisfy, so the broker was stuck. The fix is on the EIN itself:
// correct it, or (on the bond flow) stop pushing to the account record.
//
// TOLERANT READER, deliberately: `code` and `field` are the contract's only
// guarantees, so every account detail is optional and a malformed one is
// dropped rather than thrown on (NN #7). Not a `ServerErrorCode` member: that
// union is pinned against PMS's published enum, which gains this code with
// PMS-S758 — the union types what BRK recognises from the PUBLISHED spec.

export const EIN_ALREADY_EXISTS = "EIN_ALREADY_EXISTS";

/** The portal's sentence on the EIN field. The server's `error` is not shown. */
export const EIN_CONFLICT_COPY =
  "Another account in your brokerage already has this EIN.";

/** The step-level line on the bond flow when the broker can opt out of the
 *  push — the way forward without a reload. */
export const EIN_CONFLICT_PUSH_HINT =
  "Correct the EIN, or untick “Push changes to my account record” to save without updating the account.";

/** The field the contract pins, used when a refusal names none. */
const DEFAULT_FIELD = "business.ein";

/** The account that already holds the EIN, as far as the refusal says. */
export interface EinHolder {
  id: string;
  accountNumber?: string;
  legalName?: string;
}

export interface EinConflict {
  /** The request path the refusal names (`business.ein`), or null. */
  field: string | null;
  /** Null when the refusal names no account id — then nothing is linked. */
  account: EinHolder | null;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/** The EIN conflict a refusal reports, or null for any other failure. */
export function einConflictOf(err: unknown): EinConflict | null {
  if (!(err instanceof ApiError)) return null;
  if (apiErrorCode(err) !== EIN_ALREADY_EXISTS) return null;
  // `apiErrorCode` has already established `body.details` is an object.
  const details = (err.body as { details: Record<string, unknown> }).details;
  const id = text(details.accountId);
  const accountNumber = text(details.accountNumber);
  const legalName = text(details.legalName);
  return {
    field: text(details.field) ?? null,
    account:
      id === undefined
        ? null
        : {
            id,
            ...(accountNumber !== undefined ? { accountNumber } : {}),
            ...(legalName !== undefined ? { legalName } : {}),
          },
  };
}

/**
 * Which rendered control carries the conflict: the field the refusal names, or
 * the contract's `business.ein` when it names none. Null when the step drew
 * neither — the sentence then stands at step level.
 */
export function einConflictPlacement(
  err: unknown,
  fieldNames: readonly string[],
): string | null {
  const conflict = einConflictOf(err);
  if (conflict === null) return null;
  const rendered = new Set(fieldNames);
  for (const candidate of [conflict.field, DEFAULT_FIELD]) {
    if (candidate !== null && rendered.has(candidate)) return candidate;
  }
  return null;
}

/** The link's words: the holder's name, else its number, else "account". */
export function einHolderLinkText(holder: EinHolder): string {
  return `Open ${holder.legalName ?? holder.accountNumber ?? "that account"}`;
}
